import React from 'react'
import { useTheme } from '@mui/material'
import { useAppSelector } from '@SonarCX/hooks/redux'

import { AdvancedValue } from '@SonarCX/pages/Customers/FieldFilters/FieldPills'

import BaseModal from '../ChatModals/BaseModal2'
import Button from '../../atoms/Button2'
import MediaAvatar from '../../atoms/MediaAvatar'

import {
  DUE_RANGE,
  LEADS_FOR_USER,
  TASKS_FOR_USER,
  TASKS_FOR_WATCHER,
  TASKS_OTHER_OWNER
} from '@SonarCX/pages/Tasks/Tabs/AllTasksTab/helpers/parse'
import { formatDate_MMDDYYYY } from '@SonarCX/utilities'
import { useBreakpoint } from '../../../providers/BreakpointProvider'
import { useUserAPI } from '../../../providers/UserAPIProvider'
import { selectTaskStatus } from '@SonarCX/slices/taskStatus'
import { selectTaskPriority } from '@SonarCX/slices/priority'
import { selectTaskType } from '@SonarCX/slices/taskType'
import { selectCategory } from '@SonarCX/slices/category'
import { selectCustomFields } from '@SonarCX/slices/customFields'
import { selectTeams } from '@SonarCX/slices/teamsSlice'
import useStyles from './styles'

const valueMaps = {
  ownerValue: 'Owner',
  leadValue: 'Lead',
  taskValue: 'Assigned to',
  watcherValue: 'Watchers',
  categoryValue: 'Categories',
  statusValue: 'Status',
  priorityValue: 'Priorities',
  typeValue: 'Types',
  teamValue: 'Teams',
  createdValue: 'Created date',
  dueValue: 'Due date',
  completeValue: 'Completed status',
  clientValue: 'Client'
}

type TasksFilterModalProps = {
  selected: any
  selectedFields: AdvancedValue[]
  onClose: (e: any) => void
  onSave: (name: string) => void
}

const getDate = date => {
  if (date === 'null') {
    return 'no date set'
  }

  return formatDate_MMDDYYYY(date)
}

const TaskFilterUser = ({ id }) => {
  const { allUsers } = useUserAPI()

  const user = React.useMemo(() => allUsers[id], [allUsers, id])

  const classes = useStyles()

  if (!user) {
    return null
  }

  return (
    <div className={classes.taskUserContainer}>
      <MediaAvatar hideCard user={user} className={classes.smallAvatar} />
      <label style={{ marginLeft: 4 }}>{user?.name}</label>
    </div>
  )
}

const TasksFilterModal: React.FC<TasksFilterModalProps> = ({
  selected,
  selectedFields,
  onClose,
  onSave
}) => {
  const theme = useTheme()

  const { statuses } = useAppSelector(selectTaskStatus)
  const { priorities } = useAppSelector(selectTaskPriority)
  const { typesValues: types } = useAppSelector(selectTaskType)
  const { categoriesValues: categories } = useAppSelector(selectCategory)
  const { customFields: fields } = useAppSelector(selectCustomFields)
  const { teams } = useAppSelector(selectTeams)

  const { isMedium } = useBreakpoint()

  const foundFields = React.useMemo(() => {
    const found = selectedFields.map(({ field_id }) =>
      fields.find(field => field.id === field_id)
    )
    return found.filter(Boolean)
  }, [selectedFields, fields])

  const [name, setName] = React.useState('')
  const mappedValues = React.useMemo(() => {
    return Object.entries(selected).reduce((accumulated, current): any => {
      const [key, value]: [string, any] = current
      if (key === 'searchFilter') {
        return accumulated
      }

      if (value === 'null') {
        return accumulated
      }

      switch (key) {
        case 'statusValue':
          accumulated[valueMaps[key]] = statuses
            .filter(status => value.some(val => val === status.id))
            .map(x => x.name)
          return accumulated
        case 'categoryValue':
          accumulated[valueMaps[key]] = categories
            .filter(status => value.some(val => val === status.id))
            .map(x => x.name)
          return accumulated
        case 'priorityValue':
          accumulated[valueMaps[key]] = priorities
            .filter(status => value.some(val => val === status.id))
            .map(x => x.name)
          return accumulated
        case 'typeValue':
          accumulated[valueMaps[key]] = types
            .filter(status => value.some(val => val === status.id))
            .map(x => x.name)
          return accumulated
        case 'teamValue':
          accumulated[valueMaps[key]] = teams
            .filter(team => value.some(val => val === team.id))
            .map(x => x.name)
          return accumulated
        case 'completeValue':
          if (value !== 'View open and completed') {
            accumulated[valueMaps[key]] = value
          }

          return accumulated
        case 'clientValue':
        case 'createdValue':
        case 'taskValue':
        case 'dueValue':
        case 'ownerValue':
        case 'leadValue':
        case 'watcherValue':
          accumulated[valueMaps[key]] = value
          return accumulated
        default:
          return accumulated
      }
    }, {})
  }, [selected, statuses, categories, priorities, types, teams])

  const classes = useStyles()

  return (
    // @ts-ignore
    <BaseModal
      open
      title='Save Filter'
      onClose={onClose}
      PaperProps2={{
        style: {
          padding: '20px 16px 24px 16px',
          border: '1px solid #C6C6C6',
          borderRadius: '4px',
          width: isMedium ? 'calc(100% - 4rem)' : '100%',
          maxWidth: isMedium ? 'unset' : '360px',
          height: isMedium && `auto`,
          maxHeight: isMedium && 'unset',
          minHeight: isMedium && 'auto'
        }
      }}
      classes={{ title: classes.modalTitle, close: classes.modalClose }}
    >
      {selected.searchFilter && (
        <>
          <div className={classes.title}>Search</div>

          <div className={classes.content}>
            <div className={classes.row}>{selected.searchFilter}</div>
          </div>
        </>
      )}

      {Object.keys(mappedValues).length > 0 && (
        <>
          <div className={classes.title}>Selected Filters</div>

          <div className={classes.content}>
            {Object.entries(mappedValues).map((val): any => {
              const [key, value]: [string, any] = val

              return (
                <div key={key} className={classes.row}>
                  <label
                    style={{ fontWeight: theme.bold.bold }}
                  >{`${key}:`}</label>

                  <br />

                  <label>
                    {Array.isArray(value) ? value.join(', ') : value}
                  </label>

                  {key === 'Assigned to' &&
                    selected.taskValue === TASKS_FOR_USER && (
                      <TaskFilterUser id={selected.taskID} />
                    )}

                  {key === 'Watchers' &&
                    selected.watcherValue === TASKS_FOR_WATCHER && (
                      <TaskFilterUser id={selected.watcherID} />
                    )}

                  {key === 'Owner' &&
                    selected.ownerValue === TASKS_OTHER_OWNER && (
                      <TaskFilterUser id={selected.ownerID} />
                    )}

                  {key === 'Lead' && selected.leadValue === LEADS_FOR_USER && (
                    <TaskFilterUser id={selected.leadID} />
                  )}

                  {key === 'Due date' && selected.dueValue === DUE_RANGE && (
                    <div>
                      From {getDate(selected.dueStartValue)} to{' '}
                      {getDate(selected.dueEndValue)}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {foundFields.length > 0 && (
        <>
          <div className={classes.title}>Selected Fields</div>

          <div className={classes.content}>
            <div className={classes.row}>
              {foundFields.map(field => field.title).join(', ')}
            </div>
          </div>
        </>
      )}

      <div className={classes.name}>Name</div>

      <input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder='Enter filter name'
        className={classes.input}
      />

      <Button
        onClick={() => onSave(name)}
        disabled={name.trim().length === 0}
        className={classes.button}
      >
        Save Filter
      </Button>
    </BaseModal>
  )
}

export default TasksFilterModal
